import { Form } from 'antd';
import classNames from 'classnames';
import { Field, useField, useFormikContext } from 'formik';
import React, { forwardRef, memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import '../../styles.scss';
import { ControlledInputProps } from '../../types';
import CustomAutoComplete from '../AutoComplete';
import CustomDatePicker from '../DatePicker';
import CustomInput from '../Input';
import CustomTextAreaInput from '../Input/CustomTextArea';
import CustomNumberInput from '../Input/NumberInput';
import PasswordInput from '../Input/PasswordInput';
import CustomRadioGroup from '../RadioGroup';
import CustomSelect from '../Select';
import CustomImageUpload from '../UploadImage';
import CustomCheckboxGroup from '../checkboxGroup';
import CustomCheckbox from '../controls/Checkbox';
import CustomSwitcher from '../controls/Switcher';
import './styles.scss';
import CustomTimePicker from '../TimePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

const ControlledInput: React.FC<ControlledInputProps> = forwardRef(
  (props, ref) => {
    const {
      name,
      label,
      onBlur,
      warning,
      onChange,
      className,
      style = {},
      type = 'default',
      required = false,
      hideLabel = false,
      hideInput = false,
      hideHelper = false,
      inputType = 'input',
      customFormProps = {},
      preventSetValue = false,
      ...rest
    } = props;
    const { t } = useTranslation();

    const [field, meta, helpers] = useField(name);
    const { touched, error } = meta;
    const { setValue, setTouched } = helpers;
    const { submitCount } = useFormikContext();
    const showError = touched || submitCount > 0;

    const replaceOnChange = useCallback(
      (value: string | number | Date) => {
        if (!preventSetValue) setValue(value);
        onChange?.(value);
      },
      [onChange, setValue, preventSetValue],
    );

    const handleBlur = useCallback(
      (e: any) => {
        if (onBlur) {
          onBlur(e);
        }
        field.onBlur(e);
        setTouched(true);
      },
      [field, onBlur, setTouched],
    );

    const Input = useMemo(() => {
      switch (inputType) {
        case 'select':
          return CustomSelect;
        case 'password':
          return PasswordInput;
        case 'switch':
          return CustomSwitcher;
        case 'checkbox':
          return CustomCheckbox;
        case 'checkboxGroup':
          return CustomCheckboxGroup;
        case 'radioGroup':
          return CustomRadioGroup;
        case 'datePicker':
          return CustomDatePicker;
        case 'timePicker':
          return CustomTimePicker;
        case 'number':
          return CustomNumberInput;
        case 'text-area':
          return CustomTextAreaInput;
        case 'auto-complete':
          return CustomAutoComplete;
        case 'upload':
          return CustomImageUpload;
        default:
          return CustomInput;
      }
    }, [inputType]);

    const FormItem = () => (
      <Form.Item
        style={style}
        validateStatus={
          warning ? 'warning' : showError ? (error ? 'error' : '') : ''
        }
        help={showError && t(error || warning || '')}
        label={
          hideLabel ? (
            '_'
          ) : required ? (
            <>
              {label}
              <span className="mark-required">*</span>
            </>
          ) : (
            label
          )
        }
        className={classNames(className, 'controlled-form-item', {
          'hide-label': hideLabel,
          'hide-input': hideInput,
          'hide-helper': hideHelper,
        })}
        {...customFormProps}
      >
        <Input
          {...rest}
          {...field}
          ref={ref}
          type={type}
          onBlur={handleBlur}
          className="controlledInput"
          onChange={replaceOnChange as any}
        />
      </Form.Item>
    );

    return <Field name={name}>{FormItem}</Field>;
  },
);

export default memo(ControlledInput);
